import { useEffect, useState } from 'react';
import { runtimeConfig } from '../config/runtimeConfig';
import { DOWNLOADABLE_APP_URL } from '../config/appLinks';
import { DESKTOP_BOTS_BASELINE } from '../config/statusBaseline';
import { useLanguage } from './i18n/useLanguage';

interface PublicStatus {
  hosts_online: number;
  slots_running: number;
  downloadable_running?: number;
  checked_at: string;
}

const UI = {
  en: {
    title: 'Service Status', sub: 'Real-time status of White Bot infrastructure.',
    operational: 'All systems operational', degraded: 'Partial degradation', outage: 'Service disruption',
    components: 'Components',
    webPanel: 'Web panel', webPanelDesc: 'Control panel and user authentication',
    botEngine: 'Bot engine', botEngineDesc: 'Automation instances running in the cloud',
    database: 'Database', databaseDesc: 'Supabase — data and real-time events',
    hostsOnline: 'Hosts online', slotsRunning: 'Cloud bots', downloadableRunning: 'Desktop bots',
    incidents: 'Incidents (last 30 days)', noIncidents: 'No incidents reported.',
    updated: 'Updated', checking: 'Checking…', error: 'Could not retrieve live status.',
    home: 'Home', enter: 'Log in',
  },
  es: {
    title: 'Estado del servicio', sub: 'Estado en tiempo real de la infraestructura de White Bot.',
    operational: 'Todos los sistemas operativos', degraded: 'Degradación parcial', outage: 'Interrupción del servicio',
    components: 'Componentes',
    webPanel: 'Panel web', webPanelDesc: 'Panel de control y autenticación de usuarios',
    botEngine: 'Motor de bots', botEngineDesc: 'Instancias de automatización en la nube',
    database: 'Base de datos', databaseDesc: 'Supabase — datos y eventos en tiempo real',
    hostsOnline: 'Hosts online', slotsRunning: 'Bots Cloud', downloadableRunning: 'Bots descargable',
    incidents: 'Incidencias (últimos 30 días)', noIncidents: 'No se han reportado incidencias.',
    updated: 'Actualizado', checking: 'Consultando…', error: 'No se pudo obtener el estado en tiempo real.',
    home: 'Inicio', enter: 'Entrar',
  },
  pt: {
    title: 'Status do serviço', sub: 'Status em tempo real da infraestrutura do White Bot.',
    operational: 'Todos os sistemas operacionais', degraded: 'Degradação parcial', outage: 'Interrupção do serviço',
    components: 'Componentes',
    webPanel: 'Painel web', webPanelDesc: 'Painel de controle e autenticação de usuários',
    botEngine: 'Motor de bots', botEngineDesc: 'Instâncias de automação na nuvem',
    database: 'Banco de dados', databaseDesc: 'Supabase — dados e eventos em tempo real',
    hostsOnline: 'Hosts online', slotsRunning: 'Bots Cloud', downloadableRunning: 'Bots desktop',
    incidents: 'Incidentes (últimos 30 dias)', noIncidents: 'Nenhum incidente relatado.',
    updated: 'Atualizado', checking: 'Verificando…', error: 'Não foi possível obter o status em tempo real.',
    home: 'Início', enter: 'Entrar',
  },
  fr: {
    title: 'État du service', sub: 'État en temps réel de l\'infrastructure White Bot.',
    operational: 'Tous les systèmes opérationnels', degraded: 'Dégradation partielle', outage: 'Interruption de service',
    components: 'Composants',
    webPanel: 'Panneau web', webPanelDesc: 'Panneau de contrôle et authentification utilisateur',
    botEngine: 'Moteur de bots', botEngineDesc: 'Instances d\'automatisation dans le cloud',
    database: 'Base de données', databaseDesc: 'Supabase — données et événements en temps réel',
    hostsOnline: 'Hôtes en ligne', slotsRunning: 'Bots Cloud', downloadableRunning: 'Bots bureau',
    incidents: 'Incidents (30 derniers jours)', noIncidents: 'Aucun incident signalé.',
    updated: 'Mis à jour', checking: 'Vérification…', error: 'Impossible d\'obtenir le statut en temps réel.',
    home: 'Accueil', enter: 'Connexion',
  },
  de: {
    title: 'Dienststatus', sub: 'Echtzeitstatus der White-Bot-Infrastruktur.',
    operational: 'Alle Systeme betriebsbereit', degraded: 'Teilweise Beeinträchtigung', outage: 'Dienstunterbrechung',
    components: 'Komponenten',
    webPanel: 'Web-Panel', webPanelDesc: 'Kontrollpanel und Nutzer-Authentifizierung',
    botEngine: 'Bot-Engine', botEngineDesc: 'Automatisierungsinstanzen in der Cloud',
    database: 'Datenbank', databaseDesc: 'Supabase — Daten und Echtzeit-Ereignisse',
    hostsOnline: 'Hosts online', slotsRunning: 'Cloud-Bots', downloadableRunning: 'Desktop-Bots',
    incidents: 'Vorfälle (letzte 30 Tage)', noIncidents: 'Keine Vorfälle gemeldet.',
    updated: 'Aktualisiert', checking: 'Wird geprüft…', error: 'Live-Status konnte nicht abgerufen werden.',
    home: 'Startseite', enter: 'Anmelden',
  },
};

type LoadState = 'loading' | 'ok' | 'error';

function StatusDot({ ok }: { ok: boolean }) {
  return <span className={`status-dot ${ok ? 'status-dot-ok' : 'status-dot-warn'}`} />;
}

export function StatusPage() {
  const { lang, t } = useLanguage();
  // Idiomas de la oleada 2026-07 (ru/ko/zh/ja/tr): /status detallado cae a EN
  const ui = UI[lang as keyof typeof UI] ?? UI.en;

  const [liveStatus, setLiveStatus] = useState<PublicStatus | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');

  useEffect(() => {
    const cfg = runtimeConfig();
    if (!cfg.supabaseUrl || !cfg.supabaseAnonKey) {
      setLoadState('error');
      return;
    }
    fetch(`${cfg.supabaseUrl}/rest/v1/rpc/get_public_status`, {
      method: 'POST',
      headers: {
        apikey: cfg.supabaseAnonKey,
        Authorization: `Bearer ${cfg.supabaseAnonKey}`,
        'Content-Type': 'application/json',
      },
      body: '{}',
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => { setLiveStatus(data); setLoadState('ok'); })
      .catch(() => setLoadState('error'));
  }, []);

  const hostsOnline = liveStatus?.hosts_online ?? 0;
  const slotsRunning = liveStatus?.slots_running ?? 0;
  // Desktop bots = base fija + reales (ver statusBaseline.ts) — misma cifra
  // que la landing o el enlace "ver estado completo" se contradice.
  const downloadableRunning = (liveStatus?.downloadable_running ?? 0) + DESKTOP_BOTS_BASELINE;
  const overallOk = loadState !== 'ok' || hostsOnline > 0;
  const overallLabel = loadState === 'loading'
    ? ui.checking
    : loadState === 'error'
      ? ui.operational  // fallback optimistic
      : hostsOnline > 0 ? ui.operational : ui.degraded;
  const overallClass = loadState === 'ok' && hostsOnline === 0 ? 'status-banner-warn' : 'status-banner-ok';

  const checkedAt = liveStatus?.checked_at
    ? new Date(liveStatus.checked_at).toLocaleTimeString(lang === 'es' ? 'es-ES' : 'en-GB', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="lp blog-page">
      <nav className="lp-nav">
        <a href="/" className="lp-nav-logo"><img src="/logo.png" alt="White Bot" /></a>
        <div className="lp-nav-links">
          <a href="/">{ui.home}</a>
          <a href={DOWNLOADABLE_APP_URL} className="lp-btn-outline" rel="noopener">{ui.enter}</a>
        </div>
      </nav>

      <div className="status-shell">
        <div className="status-container">
          <h1 className="status-title">{ui.title}</h1>
          <p className="status-sub">{ui.sub}</p>

          {/* Overall banner */}
          <div className={`status-banner ${overallClass}`}>
            <StatusDot ok={overallOk} />
            <span>{overallLabel}</span>
            {checkedAt && <span className="status-updated">{ui.updated}: {checkedAt}</span>}
          </div>

          {/* Live metrics */}
          {loadState === 'ok' && liveStatus && (
            <div className="status-metrics status-metrics-3">
              <div className="status-metric">
                <strong>{hostsOnline}</strong>
                <span>{ui.hostsOnline}</span>
              </div>
              <div className="status-metric">
                <strong>{slotsRunning}</strong>
                <span>{ui.slotsRunning}</span>
              </div>
              <div className="status-metric">
                <strong>{downloadableRunning}</strong>
                <span>{ui.downloadableRunning}</span>
              </div>
            </div>
          )}
          {loadState === 'error' && (
            <p className="status-error-note">{ui.error}</p>
          )}

          {/* Components */}
          <h2 className="status-section-title">{ui.components}</h2>
          <div className="status-components">
            <div className="status-component">
              <div className="status-component-left">
                <StatusDot ok={true} />
                <div>
                  <strong>{ui.webPanel}</strong>
                  <span>{ui.webPanelDesc}</span>
                </div>
              </div>
              <span className="status-component-label ok">Operational</span>
            </div>
            <div className="status-component">
              <div className="status-component-left">
                <StatusDot ok={loadState !== 'ok' || hostsOnline > 0} />
                <div>
                  <strong>{ui.botEngine}</strong>
                  <span>{ui.botEngineDesc}</span>
                </div>
              </div>
              <span className={`status-component-label ${loadState !== 'ok' || hostsOnline > 0 ? 'ok' : 'warn'}`}>
                {loadState === 'loading' ? '…' : loadState === 'error' ? 'Operational' : hostsOnline > 0 ? 'Operational' : 'Degraded'}
              </span>
            </div>
            <div className="status-component">
              <div className="status-component-left">
                <StatusDot ok={loadState !== 'error'} />
                <div>
                  <strong>{ui.database}</strong>
                  <span>{ui.databaseDesc}</span>
                </div>
              </div>
              <span className={`status-component-label ${loadState !== 'error' ? 'ok' : 'warn'}`}>
                {loadState === 'error' ? 'Unknown' : 'Operational'}
              </span>
            </div>
          </div>

          {/* Incidents */}
          <h2 className="status-section-title">{ui.incidents}</h2>
          <div className="status-no-incidents">
            <span>✓</span>
            <p>{ui.noIncidents}</p>
          </div>
        </div>
      </div>

      <footer className="lp-footer">
        <div className="lp-container lp-footer-inner">
          <span className="lp-footer-logo">White Bot</span>
          <nav className="lp-footer-links">
            <a href="/">{ui.home}</a>
            <a href={t.footer.links.terms === 'Terms of use' ? '/terms' : '/terminos'}>{t.footer.links.terms}</a>
          </nav>
          <p className="lp-footer-copy">{t.footer.copy}</p>
        </div>
      </footer>
    </div>
  );
}
